import { ModalActions } from '../../modal/components/ModalActions';
import { ModalContent } from '../../modal/components/ModalContent';
import { FC } from 'react';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { z } from 'zod';
import { ControlledFormField } from '../../form/ControlledFormField';
import { Stack } from '@mui/material';
import { useIntl } from 'react-intl';
import { JourneySelect } from '../JourneySelect';
import { PhaseSelect } from '../PhaseSelect';
import { ActivitySelect } from '../ActivitySelect';
import { FormErrors } from '../../form/FormErrors';
import { useApiMutation } from '../../query/hooks';
import { actionListHeader_createAction } from '../../actions/list/ActionListHeader';
import { graphql } from '../../gql';
import { useLocation, useNavigate } from 'react-router';
import { useModalContext } from '../../modal/ModalContext';
import { useSnackbar } from '../../store/snackbar';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useTypedPathParams } from '../../../../shared/fe/hooks/useTypedPathParams';
import { journeyDetailSchema } from '../detail/JourneyDetailPage';

const createActionModal_updateActivity = graphql(`
	mutation createActionModal_updateActivity($activityId: UUID!, $actionId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { actions: { connect: { id: $actionId } } }) {
			ok
			errorMessage
		}
	}
`);

export const createActionModalSchema = z.object({});

const createFormSchema = () =>
	z.object({
		journeyId: z.string().min(1),
		phaseId: z.string().min(1),
		activityId: z.string().min(1),
	});

export const CreateActionModal: FC = () => {
	const intl = useIntl();
	const { id: journeyId } = useTypedPathParams(journeyDetailSchema);
	const form = useTypedForm({
		schema: createFormSchema(),
		defaultValues: {
			journeyId,
		},
	});
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { closeModal } = useModalContext();

	const { mutateAsync: createAction } = useApiMutation(actionListHeader_createAction);
	const { mutateAsync: updateActivity } = useApiMutation(createActionModal_updateActivity);
	const { openSnackbar } = useSnackbar();

	const values = form.watch();

	const handleSubmit = async () => {
		let result = true;
		let error = '';
		let errorMessage: string | null | undefined = '';

		const createResult = await createAction({
			name: intl.formatMessage({ id: 'action.detail.name.placeholder' }),
			description: '',
			priority: 'medium',
			status: 'open',
			links: { values: [] },
		});

		if (!createResult.createAction.ok) {
			result = false;
			error = intl.formatMessage({ id: 'action.list.create.error' });
			errorMessage = createResult.createAction.errorMessage;
		}

		const actionId = createResult.createAction.node?.id;

		if (actionId && values.activityId) {
			const updateResult = await updateActivity({ activityId: values.activityId, actionId });

			if (!updateResult.updateActivity.ok) {
				result = false;
				error = intl.formatMessage({ id: 'journey.detail.widget.actions.add.error' });
				errorMessage = updateResult.updateActivity.errorMessage;
			}
		}

		mutationResponseHandler({
			result,
			toastMessages: {
				success: intl.formatMessage({ id: 'action.list.create.success' }),
				error,
			},
			errorMessage,
			callback: () => navigate(`/actions/${actionId}/detail?redirectURI=${pathname}`),
			openSnackbar,
		});
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
			<ModalContent title={intl.formatMessage({ id: 'form.create-action.title' })}>
				<Stack spacing={3}>
					<ControlledFormField
						form={form}
						name={'journeyId'}
						label={intl.formatMessage({ id: 'form.create-action.label.journey' })}
						description={intl.formatMessage({ id: 'form.create-action.label.journey.description' })}
						render={(props) => <JourneySelect required {...props} />}
					/>

					<ControlledFormField
						form={form}
						name={'phaseId'}
						label={intl.formatMessage({ id: 'form.create-action.label.phase' })}
						render={(props) => <PhaseSelect required journeyId={values.journeyId} {...props} />}
					/>

					<ControlledFormField
						form={form}
						name={'activityId'}
						label={intl.formatMessage({ id: 'form.create-action.label.activity' })}
						render={(props) => <ActivitySelect required phaseId={values.phaseId} {...props} />}
					/>

					<FormErrors form={form} />
				</Stack>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={handleSubmit}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.create' })}
				disablePrimaryButton={!form.formState.isValid || form.formState.isSubmitting}
			/>
		</form>
	);
};
